import React, { useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { getEffectiveRole } from '../../utils/roles';
import {
    HomeIcon,
    UserGroupIcon,
    UserIcon,
    BuildingOfficeIcon,
    BuildingOffice2Icon,
    MapIcon,
    UsersIcon,
    ChartBarIcon,
    CogIcon,
    XMarkIcon,
    ClockIcon,
    MapPinIcon,
    ChatBubbleLeftRightIcon,
    ArrowPathIcon,
    DocumentCheckIcon,
    EnvelopeIcon,
    ShieldCheckIcon,
    CalendarDaysIcon,
    DocumentChartBarIcon,
    LinkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DocumentTextIcon,
    IdentificationIcon,
    DocumentDuplicateIcon,
    BanknotesIcon,
    CreditCardIcon,
    KeyIcon,
    TicketIcon,
    WrenchScrewdriverIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline';

// Static Navigation Metadata Structure
const NAVIGATION_SECTIONS = [
    {
        title: 'OVERVIEW',
        items: [
            {
                name: 'Dashboard',
                href: '/dashboard',
                icon: HomeIcon,
                permission: 'dashboard.view',
                exact: true
            }
        ]
    },
    {
        title: 'SALES',
        items: [
            {
                name: 'Leads',
                href: '/leads',
                icon: UserGroupIcon,
                permission: 'leads.view',
                match: (pathname, search) => pathname === '/leads' && !search.includes('tab=contacted')
            },
            {
                name: 'Contacted Leads',
                href: '/leads?tab=contacted',
                icon: ChatBubbleLeftRightIcon,
                permission: 'leads.view',
                match: (pathname, search) => pathname.startsWith('/leads') && search.includes('tab=contacted')
            },
            {
                name: 'Customers',
                href: '/customers',
                icon: UsersIcon,
                permission: 'customers.view',
                match: (pathname) => pathname.startsWith('/customers')
            },
            {
                name: 'Follow-ups',
                href: '/followups',
                icon: ClockIcon,
                permission: 'followups.view',
                match: (pathname) => pathname.startsWith('/followups')
            },
            {
                name: 'Site Visits',
                href: '/sitevisits',
                icon: MapPinIcon,
                permission: 'sitevisits.view',
                match: (pathname) => pathname.startsWith('/sitevisits')
            }
        ]
    },
    {
        title: 'INVENTORY',
        items: [
            {
                name: 'Projects',
                href: '/projects',
                icon: BuildingOfficeIcon,
                permission: 'projects.view',
                match: (pathname) => pathname.startsWith('/projects')
            },
            {
                name: 'Locations',
                href: '/locations',
                icon: MapPinIcon,
                permission: 'locations.view',
                match: (pathname) => pathname.startsWith('/locations')
            },
            {
                name: 'Inventory',
                href: '/inventory',
                icon: BuildingOffice2Icon,
                permission: 'inventory.view',
                match: (pathname) => pathname.startsWith('/inventory')
            }
        ]
    },
    {
        title: 'POST SALES',
        items: [
            {
                name: 'Post-Sales Overview',
                href: '/post-sales',
                icon: DocumentCheckIcon,
                permission: 'postsales.view',
                exact: true
            },
            {
                name: 'Bookings',
                href: '/post-sales/bookings',
                icon: DocumentTextIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/bookings')
            },
            {
                name: 'Construction',
                href: '/post-sales/construction',
                icon: WrenchScrewdriverIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/construction')
            },
            {
                name: 'KYC',
                href: '/post-sales/kyc',
                icon: IdentificationIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/kyc')
            },
            {
                name: 'Agreements',
                href: '/post-sales/agreements',
                icon: DocumentDuplicateIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/agreements')
            },
            {
                name: 'Loans',
                href: '/post-sales/loans',
                icon: BanknotesIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/loans')
            },
            {
                name: 'Payments',
                href: '/post-sales/payments',
                icon: CreditCardIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/payments')
            },
            {
                name: 'Possession',
                href: '/post-sales/possession',
                icon: KeyIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/possession')
            },
            {
                name: 'NOC',
                href: '/post-sales/noc',
                icon: CheckBadgeIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/noc')
            },
            {
                name: 'Tickets',
                href: '/post-sales/tickets',
                icon: TicketIcon,
                permission: 'postsales.view',
                match: (pathname) => pathname.startsWith('/post-sales/tickets')
            },
            {
                name: 'Resale Portal',
                href: '/resale-portal',
                icon: BuildingOfficeIcon,
                permission: 'brokerportal.view',
                match: (pathname) => pathname.startsWith('/resale-portal')
            }
        ]
    },
    {
        title: 'COMMUNICATIONS',
        items: [
            {
                name: 'Communications',
                href: '/communications',
                icon: ChatBubbleLeftRightIcon,
                permission: 'communications.view',
                match: (pathname) => pathname.startsWith('/communications')
            },
            {
                name: 'Marketing Automation',
                href: '/marketing-automation',
                icon: EnvelopeIcon,
                permission: 'marketing.view',
                match: (pathname) => pathname.startsWith('/marketing-automation')
            }
        ]
    },
    {
        title: 'FIELD FORCE',
        items: [
            {
                name: 'Attendance',
                href: '/attendance',
                icon: CalendarDaysIcon,
                permission: 'reports.view',
                match: (pathname) => pathname.startsWith('/attendance')
            },
            {
                name: 'Live Tracking',
                href: '/tracking/live',
                icon: MapIcon,
                permission: 'tracking.view',
                match: (pathname) => pathname.startsWith('/tracking/live')
            }
        ]
    },
    {
        title: 'MANAGEMENT',
        items: [
            {
                name: 'Users',
                href: '/users',
                icon: UserIcon,
                permission: 'users.view',
                match: (pathname) => pathname.startsWith('/users') && !pathname.includes('pending-approvals') && !pathname.includes('/team')
            },
            {
                name: 'My Team',
                getHref: (user) => (user?._id ? `/users/${user._id}/team` : '/users'),
                icon: UserGroupIcon,
                customShow: (effectiveRole, user) => effectiveRole === 'team_manager' && !!user?._id,
                match: (pathname, search, user) => pathname === `/users/${user?._id}/team`
            },
            {
                name: 'Pending Approvals',
                href: '/users/pending-approvals',
                icon: ClockIcon,
                permission: 'users.view',
                customShow: (effectiveRole) => effectiveRole === 'super_admin' || effectiveRole === 'admin',
                match: (pathname) => pathname.includes('pending-approvals')
            },
            {
                name: 'Lead Reassignment',
                href: '/lead-reassignment',
                icon: ArrowPathIcon,
                permission: 'leads.assign',
                customShow: (effectiveRole) => ['super_admin', 'admin', 'team_manager'].includes(effectiveRole),
                match: (pathname) => pathname.startsWith('/lead-reassignment')
            },
            {
                name: 'Reports',
                href: '/reports',
                icon: ChartBarIcon,
                permission: 'reports.view',
                match: (pathname) => pathname.startsWith('/reports')
            },
            {
                name: 'Scheduled Reports',
                href: '/scheduled-reports',
                icon: DocumentChartBarIcon,
                permission: 'reports.view',
                match: (pathname) => pathname.startsWith('/scheduled-reports')
            }
        ]
    },
    {
        title: 'SYSTEM',
        items: [
            {
                name: 'Roles & Permissions',
                href: '/settings/roles-permissions',
                icon: ShieldCheckIcon,
                permission: 'roles.view',
                match: (pathname) => pathname.startsWith('/settings/roles-permissions')
            },
            {
                name: 'Integrations',
                href: '/settings/integrations',
                icon: LinkIcon,
                permission: 'settings.view',
                match: (pathname) => pathname.startsWith('/settings/integrations')
            },
            {
                name: 'Settings',
                href: '/settings',
                icon: CogIcon,
                permission: 'settings.view',
                customShow: (effectiveRole) => effectiveRole === 'super_admin',
                match: (pathname) => pathname.startsWith('/settings') &&
                    !pathname.startsWith('/settings/roles-permissions') &&
                    !pathname.startsWith('/settings/integrations')
            },
            {
                name: 'Profile',
                href: '/profile',
                icon: UserIcon,
                exact: true
            }
        ]
    }
];

const Sidebar = ({ isOpen, onClose, isCollapsed = false, onToggleCollapse }) => {
    const location = useLocation();
    const { user } = useAuth();
    const { hasPermission } = usePermissions();

    const effectiveRole = useMemo(() => {
        return (getEffectiveRole(user) || '').toLowerCase().replace(/\s+/g, '_');
    }, [user]);

    const isSuperAdmin = useMemo(() => {
        return effectiveRole === 'super_admin' || user?.permissions === 'all';
    }, [effectiveRole, user?.permissions]);

    // Check permission helper safely
    const checkItemPermission = useCallback((item) => {
        if (item.customShow && !item.customShow(effectiveRole, user)) {
            return false;
        }
        if (!item.permission) {
            return true;
        }
        if (isSuperAdmin) {
            return true;
        }
        return hasPermission(item.permission);
    }, [effectiveRole, user, isSuperAdmin, hasPermission]);

    // Filter visible sections & items based on permissions
    const visibleSections = useMemo(() => {
        return NAVIGATION_SECTIONS.map((section) => {
            const filteredItems = section.items.filter(checkItemPermission);
            return {
                title: section.title,
                items: filteredItems
            };
        }).filter((section) => section.items.length > 0);
    }, [checkItemPermission]);

    // Active route detector helper
    const isItemActive = useCallback((item) => {
        const pathname = location.pathname;
        const search = location.search;

        if (item.match) {
            return item.match(pathname, search, user);
        }
        if (item.exact) {
            return pathname === item.href;
        }
        const targetHref = item.getHref ? item.getHref(user) : item.href;
        return pathname.startsWith(targetHref);
    }, [location.pathname, location.search, user]);

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-30 md:hidden transition-opacity duration-200"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Enterprise Sidebar */}
            <aside
                data-tour="sidebar"
                aria-label="Main Navigation"
                className={`
                    fixed z-40 top-0 left-0 h-full bg-slate-900 text-slate-200 border-r border-slate-800/80
                    transform transition-[width,transform] duration-200 ease-in-out
                    flex flex-col flex-shrink-0 min-h-0 shadow-2xl select-none
                    ${isOpen ? 'translate-x-0 w-72 max-w-[85vw]' : '-translate-x-full w-72'}
                    md:relative md:translate-x-0 md:z-auto md:h-full md:max-w-none md:shadow-none
                    ${isCollapsed ? 'md:w-20' : 'md:w-72'}
                `}
            >
                {/* Brand Header */}
                <div className={`flex h-16 shrink-0 items-center border-b border-slate-800/80 bg-slate-950/60 transition-all duration-200 ${isCollapsed ? 'px-3 justify-center' : 'px-5 justify-between'}`}>
                    <Link to="/dashboard" className="flex items-center gap-3 min-w-0" title="BookMyAcre Dashboard">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 via-blue-600 to-sky-400 flex items-center justify-center shadow-lg shadow-indigo-500/20 border border-indigo-400/20 shrink-0">
                            <BuildingOfficeIcon className="h-5 w-5 text-white" />
                        </div>
                        {!isCollapsed && (
                            <div className="truncate">
                                <span className="text-base font-extrabold text-white tracking-tight">
                                    Book<span className="text-indigo-400">My</span>Acre
                                </span>
                                <span className="block text-[10px] uppercase font-bold tracking-widest text-slate-400">
                                    Enterprise CRM
                                </span>
                            </div>
                        )}
                    </Link>

                    {/* Desktop Collapse / Expand Toggle Button */}
                    {onToggleCollapse && (
                        <button
                            type="button"
                            onClick={onToggleCollapse}
                            className={`hidden md:flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors border border-slate-800/80 ${isCollapsed ? 'mt-0' : ''}`}
                            title={isCollapsed ? 'Expand Sidebar (Ctrl+\\)' : 'Collapse Sidebar (Ctrl+\\)'}
                            aria-label={isCollapsed ? 'Expand sidebar navigation' : 'Collapse sidebar navigation'}
                        >
                            {isCollapsed ? (
                                <ChevronRightIcon className="h-4 w-4" />
                            ) : (
                                <ChevronLeftIcon className="h-4 w-4" />
                            )}
                        </button>
                    )}

                    {/* Mobile Close Drawer Button */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                        aria-label="Close sidebar navigation"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Navigation Sections */}
                <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-5 overscroll-y-contain custom-scrollbar">
                    {visibleSections.map((section) => (
                        <div key={section.title} className="space-y-1">
                            {/* Section Title Header */}
                            {!isCollapsed ? (
                                <h3 className="px-3 text-[10px] font-bold tracking-wider text-slate-500 uppercase select-none pb-1">
                                    {section.title}
                                </h3>
                            ) : (
                                <div className="h-px bg-slate-800/60 my-2 mx-2" aria-hidden="true" />
                            )}

                            {/* Section Items */}
                            {section.items.map((item) => {
                                const Icon = item.icon;
                                const active = isItemActive(item);
                                const itemHref = item.getHref ? item.getHref(user) : item.href;

                                return (
                                    <Link
                                        key={item.name}
                                        to={itemHref}
                                        onClick={onClose}
                                        aria-current={active ? 'page' : undefined}
                                        title={isCollapsed ? item.name : undefined}
                                        data-tour={Sidebar.TOUR_ANCHOR_BY_NAME?.[item.name]}
                                        className={`
                                            group relative flex items-center text-xs font-semibold rounded-xl transition-all duration-150 outline-none
                                            ${isCollapsed ? 'justify-center px-0 py-2.5 h-10' : 'px-3 py-2.5'}
                                            ${active
                                                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md shadow-indigo-600/25 border border-indigo-400/20 font-bold'
                                                : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 focus:bg-slate-800/80 focus:text-white'
                                            }
                                        `}
                                    >
                                        <Icon
                                            aria-hidden="true"
                                            className={`
                                                h-4 w-4 transition-colors duration-150 shrink-0
                                                ${isCollapsed ? 'm-0' : 'mr-3'}
                                                ${active ? 'text-white' : 'text-slate-400 group-hover:text-indigo-400'}
                                            `}
                                        />

                                        {!isCollapsed && (
                                            <span className="truncate">{item.name}</span>
                                        )}

                                        {!isCollapsed && active && (
                                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white shadow-sm shrink-0" />
                                        )}

                                        {/* Collapsed Mode Floating Tooltip */}
                                        {isCollapsed && (
                                            <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-950 text-slate-100 text-xs font-medium rounded-lg shadow-xl border border-slate-800 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 whitespace-nowrap hidden md:block">
                                                {item.name}
                                            </div>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* Authenticated User Footer */}
                <div className={`mt-auto shrink-0 border-t border-slate-800/80 bg-slate-950/60 transition-all duration-200 ${isCollapsed ? 'p-2.5' : 'p-3.5'}`}>
                    <div className={`flex items-center rounded-xl bg-slate-900/80 border border-slate-800/80 transition-all duration-200 ${isCollapsed ? 'justify-center p-2' : 'gap-3 p-2.5'}`} title={`${user?.firstName || ''} ${user?.lastName || ''} (${effectiveRole})`}>
                        <div className="relative shrink-0">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-600 to-blue-500 flex items-center justify-center shadow-md font-bold text-xs text-white border border-indigo-400/20">
                                {(user?.firstName?.charAt(0) || 'U')}{(user?.lastName?.charAt(0) || '')}
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-slate-950" />
                        </div>

                        {!isCollapsed && (
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-white truncate">
                                    {user?.firstName || 'User'} {user?.lastName || ''}
                                </p>
                                <p className="text-[10px] font-medium text-indigo-400 truncate capitalize">
                                    {(effectiveRole || 'User').replace(/_/g, ' ')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
        </>
    );
};

Sidebar.TOUR_ANCHOR_BY_NAME = {
    Dashboard: 'dashboard',
    Leads: 'leads',
    'Follow-ups': 'followups'
};

export default Sidebar;
